import { useState } from 'react';
import { supabase } from './supabase';
import TermsOfService from './TermsOfService';
import { Wordmark, VineMark, Pill, Dot, Btn, Mono, GridBg } from './components/ui';

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [mode, setMode] = useState('login');
  const [resetSent, setResetSent] = useState(false);
  const [showTos, setShowTos] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) return setMsg({ text: 'Please enter your email and password.', type: 'error' });
    setLoading(true); setMsg(null);
    const { data, error: loginError } = await supabase.auth.signInWithPassword({ email, password });
    if (loginError) setMsg({ text: loginError.message, type: 'error' });
    else onLogin(data.user);
    setLoading(false);
  };

  const handleReset = async () => {
    if (!email) return setMsg({ text: 'Please enter your email address.', type: 'error' });
    setLoading(true); setMsg(null);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    if (resetError) setMsg({ text: resetError.message, type: 'error' });
    else setResetSent(true);
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { if (mode === 'login') handleLogin(); else handleReset(); }
  };

  const switchMode = (next) => { setMode(next); setMsg(null); setShowPassword(false); };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--g-text-dim)',
    marginBottom: 6,
    display: 'block',
    fontFamily: 'Inter, sans-serif',
  };
  const inputBoxStyle = (focus) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '10px 14px',
    borderRadius: 6,
    background: 'var(--g-card-flat)',
    border: `1px solid ${focus ? 'rgba(16,185,129,0.45)' : 'var(--g-border-hi)'}`,
    transition: 'border-color 0.15s',
  });

  const ticker = [
    { p: 'Stiiizy 1g · Strawnana',     s: 'Harborside',   d: -12 },
    { p: 'Wyld 100mg · Marionberry',   s: 'Sweet Flower', d: -8 },
    { p: 'Cookies 3.5g · GMO',         s: 'STIIIZY',      d: +6 },
    { p: 'Raw Garden 1g · Mimosa',     s: 'Eaze',         d: -15 },
  ];

  return (
    <div
      style={{
        height: '100vh',
        width: '100%',
        display: 'flex',
        background: 'var(--g-bg)',
        color: 'var(--g-text)',
        fontFamily: 'Inter, sans-serif',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* ─── Left: marketing panel ────────────────────────────────────────── */}
      <div
        className="gv-login-marketing"
        style={{
          flex: '1.2',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: 56,
          position: 'relative',
          overflow: 'hidden',
          background: 'var(--g-card-flat)',
          borderRight: '1px solid var(--g-border)',
        }}
      >
        <GridBg opacity={0.6} />
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 600,
            height: 600,
            transform: 'translate(-50%, -50%)',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 65%)',
            filter: 'blur(30px)',
          }}
        />

        <div style={{ position: 'absolute', top: 56, left: 56, zIndex: 2 }}>
          <Wordmark size={20} accent="var(--g-accent)" />
        </div>

        <div style={{ position: 'relative', zIndex: 1, maxWidth: 460 }}>
          <Pill tone="vine">
            <Dot color="var(--g-accent-hi)" pulse /> Cannabis price intelligence · refreshed daily
          </Pill>
          <h1
            style={{
              margin: '20px 0 14px',
              fontSize: 36,
              fontWeight: 800,
              color: 'var(--g-text)',
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
            }}
          >
            Your competitors&apos; prices,<br />
            <span style={{ color: 'var(--g-accent-hi)', textShadow: '0 0 30px rgba(16,185,129,0.4)' }}>
              decoded daily.
            </span>
          </h1>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--g-text-dim)', lineHeight: 1.6 }}>
            Sign in to your Graphvine portal — fresh market intelligence across Dutchie, Meadow,
            iHeartJane, Treez &amp; Flowhub.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 28 }}>
            {ticker.map(r => (
              <div
                key={r.p}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '6px 10px',
                  borderRadius: 4,
                  background: 'var(--g-input)',
                  border: '1px solid var(--g-border)',
                }}
              >
                <Dot color={r.d < 0 ? 'var(--g-danger)' : 'var(--g-accent-hi)'} />
                <Mono size={12} color="var(--g-text-dim)" style={{ flex: 1 }}>{r.p}</Mono>
                <Mono size={12} color="var(--g-text-faint)">{r.s}</Mono>
                <Mono size={12} color={r.d < 0 ? 'var(--g-danger)' : 'var(--g-accent-hi)'} style={{ fontWeight: 600 }}>
                  {r.d > 0 ? '+' : ''}{r.d}%
                </Mono>
              </div>
            ))}
          </div>
        </div>

        <div style={{ position: 'absolute', bottom: 56, left: 56, zIndex: 1 }}>
          <Mono size={12} color="var(--g-text-faint)">graphvine.ai · cannabis price intelligence</Mono>
        </div>
      </div>

      {/* ─── Right: form ──────────────────────────────────────────────────── */}
      <div
        style={{
          flex: '1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 40,
          minWidth: 0,
        }}
      >
        <div style={{ width: '100%', maxWidth: 380 }}>
          <div className="gv-login-mobile-logo">
            <Wordmark size={20} accent="var(--g-accent)" />
          </div>

          <Mono size={12} color="var(--g-text-muted)" style={{ letterSpacing: '0.01em', }}>
            {mode === 'login' ? 'Sign in' : 'Reset password'}
          </Mono>
          <h2 style={{ margin: '8px 0 6px', fontSize: 24, fontWeight: 700, color: 'var(--g-text)', letterSpacing: '-0.01em' }}>
            {mode === 'login' ? 'Welcome back' : 'Forgot password'}
          </h2>
          <p style={{ margin: '0 0 24px', fontSize: 13, color: 'var(--g-text-dim)' }}>
            {mode === 'login'
              ? 'Continue to your Graphvine workspace.'
              : 'Enter your email to receive a reset link.'}
          </p>

          {resetSent ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                <VineMark size={36} color="var(--g-accent-hi)" />
              </div>
              <Mono size={12} color="var(--g-accent-hi)" style={{ letterSpacing: '0.01em', fontWeight: 600 }}>
                Reset link sent
              </Mono>
              <p style={{ fontSize: 13, color: 'var(--g-text-dim)', marginTop: 8 }}>
                Check your inbox to set a new password.
              </p>
              <button
                onClick={() => { switchMode('login'); setResetSent(false); }}
                style={{
                  marginTop: 16,
                  fontSize: 12,
                  color: 'var(--g-text-dim)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                ← Back to login
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Email */}
                <div>
                  <label style={labelStyle}>Email</label>
                  <div style={inputBoxStyle()}>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="you@dispensary.com"
                      autoComplete="email"
                      style={{
                        flex: 1,
                        background: 'transparent',
                        border: 'none',
                        outline: 'none',
                        color: 'var(--g-text)',
                        fontSize: 13,
                        fontFamily: 'Inter, sans-serif',
                        caretColor: 'var(--g-accent)',
                      }}
                    />
                  </div>
                </div>

                {/* Password */}
                {mode !== 'reset' && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <label style={{ ...labelStyle, marginBottom: 0 }}>Password</label>
                      {mode === 'login' && (
                        <button
                          onClick={() => switchMode('reset')}
                          style={{
                                                        fontSize: 12,
                            color: 'var(--g-accent-hi)',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          Forgot?
                        </button>
                      )}
                    </div>
                    <div style={inputBoxStyle()}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="••••••••"
                        autoComplete="current-password"
                        style={{
                          flex: 1,
                          background: 'transparent',
                          border: 'none',
                          outline: 'none',
                          color: 'var(--g-text)',
                          fontSize: 13,
                          fontFamily: 'Inter, sans-serif',
                          caretColor: 'var(--g-accent)',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(v => !v)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--g-text-faint)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        tabIndex={-1}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                          {showPassword ? 'visibility_off' : 'visibility'}
                        </span>
                      </button>
                    </div>
                  </div>
                )}

                {msg && (
                  <p
                    style={{
                                            fontSize: 12,
                      padding: '8px 12px',
                      borderRadius: 6,
                      margin: 0,
                      background:
                        msg.type === 'success'
                          ? 'rgba(16,185,129,0.10)'
                          : 'rgba(239,68,68,0.10)',
                      border:
                        msg.type === 'success'
                          ? '1px solid rgba(16,185,129,0.30)'
                          : '1px solid rgba(239,68,68,0.30)',
                      color: msg.type === 'success' ? 'var(--g-accent-hi)' : 'var(--g-danger)',
                    }}
                  >
                    {msg.text}
                  </p>
                )}

                <Btn
                  variant="primary"
                  size="lg"
                  onClick={mode === 'login' ? handleLogin : handleReset}
                  disabled={loading}
                  style={{ justifyContent: 'center', width: '100%', marginTop: 4 }}
                >
                  {loading
                    ? 'Loading…'
                    : mode === 'login'
                    ? 'Sign in →'
                    : 'Send reset link →'}
                </Btn>
              </div>

              {/* Mode switcher */}
              {mode === 'reset' && (
                <div style={{ marginTop: 20, textAlign: 'center' }}>
                  <button
                    onClick={() => switchMode('login')}
                    style={{
                      fontSize: 12,
                      color: 'var(--g-text-dim)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    ← Back to login
                  </button>
                </div>
              )}

              <div style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid var(--g-border)' }}>
                <Mono size={11} color="var(--g-text-faint)" style={{ letterSpacing: '0.01em', }}>
                  Trusted by
                </Mono>
                <div
                  style={{
                    display: 'flex',
                    gap: 14,
                    marginTop: 8,
                    color: 'var(--g-text-muted)',
                    fontSize: 12,
                    fontWeight: 500,
                    opacity: 0.7,
                  }}
                >
                  <span>Dutchie</span><span>·</span>
                  <span>Meadow</span><span>·</span>
                  <span>iHeartJane</span><span>·</span>
                  <span>Treez</span>
                </div>
              </div>
            </>
          )}

          <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <Mono size={11} color="var(--g-text-faint)" style={{ letterSpacing: '0.01em', }}>
              © 2026 Graphvine
            </Mono>
            <button
              onClick={() => setShowTos(true)}
              style={{
                                fontSize: 11,
                color: 'var(--g-text-faint)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                letterSpacing: '0.01em',                               }}
            >
              Terms of Service
            </button>
          </div>
        </div>
      </div>

      {showTos && <TermsOfService onClose={() => setShowTos(false)} />}
    </div>
  );
}
